import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUpdateOpportunityNotes } from '@/hooks/api/useOpportunities';
import { formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { useToast } from '@/providers/toast-provider';

/**
 * Mirrors OPPORTUNITY_NOTES_MAX_LENGTH in @fig/shared, deliberately duplicated
 * rather than imported. `@fig/shared` compiles to CommonJS and is a linked
 * workspace package, so Vite serves it to the browser unconverted — a named
 * runtime import from it fails at parse time with "does not provide an export
 * named ...". Only `import type` from that package is safe here.
 *
 * This value is a UX affordance (textarea limit and counter). The authoritative
 * cap is enforced by the API's route schema, which does import the shared one.
 */
const NOTES_MAX_LENGTH = 4000;

// Show the counter only once the note is long enough for the cap to matter.
const COUNTER_VISIBLE_FROM = NOTES_MAX_LENGTH - 500;

interface OpportunityNotesProps {
  opportunityId: string;
  notes: string | null;
  notesUpdatedAt: string | null;
  notesUpdatedBy: string | null;
}

export function OpportunityNotes({
  opportunityId,
  notes,
  notesUpdatedAt,
  notesUpdatedBy,
}: OpportunityNotesProps) {
  const savedValue = notes ?? '';
  const [draft, setDraft] = useState(savedValue);
  const updateNotes = useUpdateOpportunityNotes();
  const { showToast } = useToast();

  // Re-sync when the server value changes (refetch, or another tab saving), but
  // only while the user has nothing in flight — never clobber an active edit.
  useEffect(() => {
    setDraft(savedValue);
  }, [savedValue]);

  const isDirty = draft !== savedValue;
  const remaining = NOTES_MAX_LENGTH - draft.length;
  const showCounter = draft.length >= COUNTER_VISIBLE_FROM;

  const handleSave = async () => {
    try {
      const result = await updateNotes.mutateAsync({ id: opportunityId, notes: draft });
      showToast({
        title: result.notes === null ? 'Note cleared' : 'Note saved',
        description:
          result.notes === null
            ? 'This opportunity no longer has a note.'
            : 'Your note is visible to everyone with access to this opportunity.',
      });
    } catch (err) {
      console.error('Failed to save opportunity notes:', err);
      showToast({
        title: 'Unable to save note',
        description: 'Your text is still here — try again or refresh the page.',
      });
    }
  };

  return (
    <Card style={{ boxShadow: 'var(--inset-shadow-deep)' }}>
      <CardHeader className="pb-2">
        <CardTitle>Notes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={NOTES_MAX_LENGTH}
          rows={5}
          disabled={updateNotes.isPending}
          aria-label="Opportunity notes"
          className={cn(
            'w-full resize-y rounded-md border border-input px-3 py-2 text-sm leading-relaxed text-foreground',
            'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            updateNotes.isPending && 'cursor-not-allowed opacity-60'
          )}
          style={{ backgroundColor: 'hsl(var(--surface-0))' }}
          placeholder="Add context for this opportunity — what the advisor said, why it is or isn't a good replacement candidate, what to follow up on..."
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {notesUpdatedAt && (
              <span>
                Last edited
                {notesUpdatedBy ? ` by ${notesUpdatedBy}` : ''} &middot;{' '}
                {formatDate(notesUpdatedAt)}
              </span>
            )}
            {showCounter && (
              <span className={cn(notesUpdatedAt && 'ml-2', remaining === 0 && 'text-destructive')}>
                {remaining} characters left
              </span>
            )}
          </div>

          {isDirty && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDraft(savedValue)}
                disabled={updateNotes.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="text-white shadow-sm transition-colors hover:opacity-90"
                style={{ backgroundColor: 'hsl(211 47% 20%)' }}
                onClick={() => void handleSave()}
                disabled={updateNotes.isPending}
              >
                {updateNotes.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
